import { createSlice } from '@reduxjs/toolkit';

/**
 * Phase 2-B: Session Cookie Authentication
 * 
 * loginResponse normalized structure:
 * { user: "JSON string", defaultCurrency: "JSON string", defaultLanguage: "JSON string", apiKey: "string" }
 */

/**
 * Safely parse JSON - handles string, object, null, or undefined
 * @param {*} value - Value to parse
 * @returns {Object|null} Parsed object or null
 */
function safeJsonParse(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      console.warn('[userReducer] Failed to parse JSON:', e.message);
      return null;
    }
  }
  return null;
}

const initialState = {
  isAuthenticated: false,
  twofaStatus: false,
  loginResponse: null, // Phase 2-B: Normalized shape { user, defaultCurrency, defaultLanguage, apiKey }
  profile: { data: {} },
  selectedCurrency: {},
  selectedLanguage: {},
  twoFaAuth:{
    isEnabled:false,
    twoFadata:{}
  },
  conversionFactor: {
    currencies: [],
    selectedCurrency: null,
    defaultCurrency: null,
  },
  isTourOpen:{
    status:false,
  },
  isDemoVisitor: false
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoginResponse: (state, action) => {
      state.loginResponse = action.payload;
    },
    setIsAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    setTwofaResponse: (state, action) => {
      return {
        ...state,
        twoFaAuth: action.payload,
      };
    },
    
    setProfile: (state, action) => {
      state.profile = action.payload;
    },
    updateProfile: (state, action) => {
      const { profileDetails } = action.payload;
      const updatedProfileDetails = {
        ...state.profile.personalDetails,
        name: profileDetails.name,
        secondName: profileDetails.secondName,
        gender: profileDetails.gender
      }
      state.profile.personalDetails = updatedProfileDetails
    },
    updateContact: (state, action) => {
      const { contactDetails } = action.payload;
      const updatedContactDetails = {
        ...state.profile.contactDetails,
        address: contactDetails.address,
        address2: contactDetails.address2,
        country: contactDetails.country,
        state: contactDetails.state,
        city: contactDetails.city,
        zipCode: contactDetails.zipCode,
        email: contactDetails.email,
        mobile: contactDetails.mobile
      }
      state.profile.contactDetails = updatedContactDetails
    },
    updateBank: (state, action) => {
      const { bankDetails } = action.payload;
      const updatedBankDetails = {
        ...state.profile.bankDetails,
        bankName: bankDetails.bankName,
        branchName: bankDetails.branchName,
        holderName: bankDetails.holderName,
        accountNo: bankDetails.accountNo,
        ifsc: bankDetails.ifsc,
        pan: bankDetails.pan
      }
      state.profile.bankDetails = updatedBankDetails;
    },
    setSelectedCurrency: (state, action) => {
      if (action.payload === null) {
        // Parse from normalized loginResponse (Phase 2-B)
        const parsed = safeJsonParse(state.loginResponse?.defaultCurrency);
        if (parsed) {
          state.selectedCurrency = parsed;
        }
      } else {
        state.selectedCurrency = action.payload;
      }
    },
    setSelectedLanguage: (state, action) => {
      if (action.payload === null) {
        // Parse from normalized loginResponse (Phase 2-B)
        const languageValue = state.loginResponse?.defaultLanguage;
        const parsed = safeJsonParse(languageValue);
        if (parsed) {
          state.selectedLanguage = parsed;
          const strValue = typeof languageValue === 'string' ? languageValue : JSON.stringify(parsed);
          localStorage.setItem("userLanguage", strValue);
        }
      } else {
        state.selectedLanguage = action.payload;
        localStorage.setItem("userLanguage", JSON.stringify(action.payload));
      }
    },
    setConversionFactors: (state, action) => {
      const { currencies, selectedCurrency, defaultCurrency } = action.payload;
      state.conversionFactor.currencies = currencies;
      state.conversionFactor.selectedCurrency = selectedCurrency;
      state.conversionFactor.defaultCurrency = defaultCurrency;
    },
    updateConversionFactors: (state, action) => {
      state.conversionFactor.selectedCurrency = action.payload;
    },
    updateTourOpen: (state, action) => {
      state.isTourOpen.status = action.payload;
    },
    demoVisitorAdded: (state, action) => {
      state.isDemoVisitor = action.payload;
    },
  },
});

export const { setLoginResponse, setIsAuthenticated, setProfile, updateProfile, updateContact, updateBank, setSelectedCurrency, setSelectedLanguage, setConversionFactors, updateConversionFactors, demoVisitorAdded, updateTourOpen, setTwofaResponse } = userSlice.actions;

export default userSlice.reducer;
